import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Package, Hash, DollarSign, Layers, Info, X, Plus, Search, Loader2 } from 'lucide-react';

import { useProducts } from '../Utils/useProducts';
import { 
  productFormSchema, 
  PRODUCT_UNITS, 
  WARRANTY_PERIODS,
  sanitizeProductData 
} from '../Utils/productsSchema';

export const ProductFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  onLoading,
  mode = 'create', // 'create' or 'edit'
  product = null,
  title,
  clientId,
  availableItems = [],
  categories = [],
  fetchAvailableItems
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [formConfig, setFormConfig] = useState(null);

  const { createProduct, updateProduct } = useProducts(clientId);

  // Load form configuration
  useEffect(() => {
    const savedConfig = localStorage.getItem(`product_form_config_${clientId}`)
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig)
        setFormConfig(parsedConfig)
      } catch (error) {
        console.error('Error parsing form configuration:', error)
      }
    }
  }, [clientId])

  // Helper function to check if a field should be visible
  const isFieldVisible = (sectionId, fieldId) => {
    if (!formConfig) return true // Show all fields if no config
    const section = formConfig.sections?.[sectionId]
    if (!section || !section.visible) return false
    const field = section.fields?.[fieldId]
    return field ? field.visible : true
  }

  // Helper function to check if a section should be visible
  const isSectionVisible = (sectionId) => {
    if (!formConfig) return true // Show all sections if no config
    const section = formConfig.sections?.[sectionId]
    return section ? section.visible : true
  }

  // Get visible tabs for the tab list
  const getVisibleTabs = () => {
    const tabs = []
    if (isSectionVisible('basic')) {
      tabs.push({ value: 'basic', label: 'Información Básica', icon: Info })
    }
    if (isSectionVisible('details')) {
      tabs.push({ value: 'details', label: 'Detalles', icon: Package })
    }
    if (isSectionVisible('items')) {
      tabs.push({ value: 'items', label: `Items (${selectedItems.length})`, icon: Layers })
    }
    return tabs
  }

  const form = useForm({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      sku: "",
      price: "",
      description: "",
      items: [],
    },
  });

  // Initialize form with product data for edit mode
  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && product) {
        form.reset({
          name: product.name || "",
          sku: product.sku || "",
          price: product.price?.toString() || "",
          description: product.description || "",
          items: product.items || [],
        });
        setSelectedItems(product.items || []);
      } else {
        form.reset();
        setSelectedItems([]);
      }
      // Set the first visible tab as active
      const visibleTabs = getVisibleTabs();
      if (visibleTabs.length > 0) {
        setActiveTab(visibleTabs[0].value);
      }
      setItemSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, product, form, formConfig]);

  // Debounce para búsqueda de items
  useEffect(() => {
    if (!fetchAvailableItems || !itemSearchTerm || itemSearchTerm.length < 2) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchAvailableItems({ name: itemSearchTerm });
    }, 300); // 300ms de debounce

    return () => clearTimeout(timeoutId);
  }, [itemSearchTerm, fetchAvailableItems]);

  const handleItemSelect = (item) => {
    const itemId = item.id;
    if (!selectedItems.some((sel) => sel.id === itemId)) {
      const newItems = [
        ...selectedItems,
        { id: itemId, quantity: 1, unit: "unit", ...item },
      ];
      setSelectedItems(newItems);
      form.setValue("items", newItems);
    }
    setItemSearchTerm("");
  };

  const handleItemRemove = (itemId) => {
    const newItems = selectedItems.filter((item) => item.id !== itemId);
    setSelectedItems(newItems);
    form.setValue("items", newItems);
  };

  // Update quantity/unit for a selected item
  const handleItemChange = (itemId, field, value) => {
    const newItems = selectedItems.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    setSelectedItems(newItems);
    form.setValue("items", newItems);
  };

  // Get selected items with full data, plus quantity/unit
  const getSelectedItemsData = () => {
    return selectedItems.map((sel) => {
      const full =
        availableItems.find((item) => parseInt(item.id) === sel.id) || {};
      return { ...full, ...sel };
    });
  };

  const getFilteredItems = () => {
    return availableItems.filter((item) => {
      const matchesSearch =
        !itemSearchTerm ||
        item.name?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.sku?.toLowerCase().includes(itemSearchTerm.toLowerCase());
      const notSelected = !selectedItems.some(
        (sel) => parseInt(sel.id) === parseInt(item.id)
      );
      return matchesSearch && notSelected;
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      onLoading?.(true);

      const sanitizedData = sanitizeProductData({
        ...data,
        items: selectedItems,
      });

      let response;
      if (mode === "edit") {
        response = await updateProduct(product.id, sanitizedData);
      } else {
        response = await createProduct(sanitizedData);
      }

      if (response.success) {
        onSuccess?.(response.data);
        onClose();
      }
    } catch (error) {
      console.error(
        `Error ${mode === "edit" ? "updating" : "creating"} product:`,
        error
      );
    } finally {
      setIsSubmitting(false);
      onLoading?.(false);
    }
  };

  if (!isOpen) return null;

  const visibleTabs = getVisibleTabs();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-orange-500" />
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Completa la información para crear un nuevo producto."
              : "Modifica la información del producto."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="h-full flex flex-col"
            >
              {visibleTabs.length > 1 ? (
                <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex-1 flex flex-col"
                >
                  <TabsList
                    className={`grid w-full grid-cols-${visibleTabs.length} mb-4 flex-shrink-0`}
                  >
                    {visibleTabs.map((tab) => {
                      const IconComponent = tab.icon;
                      return (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          className="flex items-center space-x-2"
                        >
                          <IconComponent className="h-4 w-4" />
                          <span>{tab.label}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  <div className="flex-1 overflow-y-auto min-h-0">
                    {/* Basic Information Tab */}
                    {isSectionVisible("basic") && (
                      <TabsContent
                        value="basic"
                        className="mt-0 space-y-6 h-full overflow-y-auto px-1"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-4">
                            {isFieldVisible("basic", "name") && (
                              <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Nombre del Producto *</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: Laptop Dell Inspiron..."
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                            {isFieldVisible("basic", "price") && (
                              <FormField
                                control={form.control}
                                name="price"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Precio de Venta</FormLabel>
                                    <FormControl>
                                      <div className="relative">
                                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="0.00"
                                          {...field}
                                          className="pl-10 focus:border-orange-400 focus:ring-orange-400/20"
                                        />
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                          <div className="space-y-4">
                            {isFieldVisible("basic", "sku") && (
                              <FormField
                                control={form.control}
                                name="sku"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>SKU *</FormLabel>
                                    <FormControl>
                                      <div className="relative">
                                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                          placeholder="Ej: DELL-INSP-001"
                                          {...field}
                                          className="pl-10 focus:border-orange-400 focus:ring-orange-400/20"
                                        />
                                      </div>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                        </div>
                        <div className="space-y-4">
                          {isFieldVisible("basic", "description") && (
                            <FormField
                              control={form.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Descripción</FormLabel>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Describe las características y beneficios del producto..."
                                      rows={3}
                                      {...field}
                                      className="focus:border-orange-400 focus:ring-orange-400/20"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      </TabsContent>
                    )}

                    {/* Details Tab */}
                    {isSectionVisible("details") && (
                      <TabsContent
                        value="details"
                        className="mt-0 space-y-6 h-full overflow-y-auto px-1"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-4">
                            {isFieldVisible("details", "brand") && (
                              <FormField
                                control={form.control}
                                name="brand"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Marca</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: Dell, Apple, Samsung..."
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "model") && (
                              <FormField
                                control={form.control}
                                name="model"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Modelo</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: Inspiron 15 3000..."
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "stock_quantity") && (
                              <FormField
                                control={form.control}
                                name="stock_quantity"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Cantidad en Stock</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="0"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value) || 0
                                          )
                                        }
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "min_stock") && (
                              <FormField
                                control={form.control}
                                name="min_stock"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Stock Mínimo</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min="0"
                                        {...field}
                                        onChange={(e) =>
                                          field.onChange(
                                            parseInt(e.target.value) || 0
                                          )
                                        }
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "unit") && (
                              <FormField
                                control={form.control}
                                name="unit"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Unidad de Medida</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="focus:border-orange-400 focus:ring-orange-400/20">
                                          <SelectValue placeholder="Seleccionar unidad" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {PRODUCT_UNITS.map((unit) => (
                                          <SelectItem
                                            key={unit.value}
                                            value={unit.value}
                                          >
                                            {unit.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "warranty_period") && (
                              <FormField
                                control={form.control}
                                name="warranty_period"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Período de Garantía</FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger className="focus:border-orange-400 focus:ring-orange-400/20">
                                          <SelectValue placeholder="Seleccionar garantía" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {WARRANTY_PERIODS.map((period) => (
                                          <SelectItem
                                            key={period.value}
                                            value={period.value}
                                          >
                                            {period.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>

                          <div className="space-y-4">
                            {isFieldVisible("details", "weight") && (
                              <FormField
                                control={form.control}
                                name="weight"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Peso (kg)</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "dimensions") && (
                              <FormField
                                control={form.control}
                                name="dimensions"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Dimensiones</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: 30 x 20 x 5 cm"
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "color") && (
                              <FormField
                                control={form.control}
                                name="color"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Color</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: Negro, Plata, Azul..."
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}

                            {isFieldVisible("details", "material") && (
                              <FormField
                                control={form.control}
                                name="material"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Material</FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Ej: Aluminio, Plástico, Acero..."
                                        {...field}
                                        className="focus:border-orange-400 focus:ring-orange-400/20"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                        </div>

                        <div className="space-y-4">
                          {isFieldVisible("details", "notes") && (
                            <FormField
                              control={form.control}
                              name="notes"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Notas Adicionales</FormLabel>
                                  <FormControl>
                                    <Textarea
                                      placeholder="Información adicional sobre el producto..."
                                      rows={3}
                                      {...field}
                                      className="focus:border-orange-400 focus:ring-orange-400/20"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      </TabsContent>
                    )}

                    {/* Items Tab */}
                    {isSectionVisible("items") && (
                      <TabsContent
                        value="items"
                        className="mt-0 space-y-4 h-full overflow-y-auto px-1"
                      >
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center space-x-2">
                              <Layers className="h-5 w-5 text-orange-500" />
                              <span>Items Asociados</span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Search Items */}
                            {isFieldVisible("items", "items_search") && (
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                  placeholder="Escribe al menos 2 caracteres para buscar items..."
                                  value={itemSearchTerm}
                                  onChange={(e) =>
                                    setItemSearchTerm(e.target.value)
                                  }
                                  className="pl-10 focus:border-orange-400 focus:ring-orange-400/20"
                                />
                              </div>
                            )}

                            {/* Available Items */}
                            {isFieldVisible("items", "items_search") &&
                            itemSearchTerm ? (
                              itemSearchTerm.length < 2 ? (
                                <div className="p-3 text-center text-slate-400 bg-slate-50 rounded">
                                  <Search className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                                  <p className="text-sm">
                                    Escribe al menos 2 caracteres para buscar
                                  </p>
                                </div>
                              ) : (
                                <div className="max-h-40 overflow-y-auto border rounded">
                                  {getFilteredItems().length > 0 ? (
                                    <>
                                      {getFilteredItems()
                                        .slice(0, 10)
                                        .map((item) => (
                                          <div
                                            key={item.id}
                                            onClick={() =>
                                              handleItemSelect(item)
                                            }
                                            className="p-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between transition-colors"
                                          >
                                            <div className="flex-1">
                                              <div className="font-medium text-slate-900">
                                                {item.name}
                                              </div>
                                              <div className="flex items-center space-x-4 text-sm text-slate-500">
                                                {item.sku && (
                                                  <span>SKU: {item.sku}</span>
                                                )}
                                                {item.stock !== undefined && (
                                                  <span
                                                    className={`px-2 py-1 rounded text-xs ${
                                                      item.stock > 0
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"
                                                    }`}
                                                  >
                                                    Stock: {item.stock}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <Plus className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                          </div>
                                        ))}
                                      {getFilteredItems().length > 10 && (
                                        <div className="p-2 text-center text-xs text-slate-500 bg-slate-50">
                                          Mostrando 10 de{" "}
                                          {getFilteredItems().length}{" "}
                                          resultados. Refina tu búsqueda para
                                          ver más.
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="p-4 text-center text-slate-500">
                                      <Package className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                      <p>
                                        No se encontraron items con "
                                        {itemSearchTerm}"
                                      </p>
                                      <p className="text-sm text-slate-400">
                                        Intenta con otros términos de búsqueda
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )
                            ) : (
                              isFieldVisible("items", "items_search") && (
                                <div className="p-4 text-center text-slate-400 bg-slate-50 rounded">
                                  <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                  <p className="font-medium">
                                    Buscar Items del Inventario
                                  </p>
                                  <p className="text-sm">
                                    Usa el campo de búsqueda para encontrar
                                    items y asociarlos a este producto
                                  </p>
                                </div>
                              )
                            )}

                            {/* Selected Items */}
                            {isFieldVisible("items", "items_selection") && (
                              <div>
                                <h4 className="font-medium mb-2">
                                  Items Seleccionados ({selectedItems.length})
                                </h4>
                                {getSelectedItemsData().length > 0 ? (
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {getSelectedItemsData().map((item) => (
                                      <div
                                        key={item.id}
                                        className="flex items-center justify-between p-3 bg-orange-50 rounded border border-orange-200"
                                      >
                                        <div>
                                          <div className="font-medium">
                                            {item.name}
                                          </div>
                                          {item.sku && (
                                            <div className="text-sm text-slate-600">
                                              SKU: {item.sku}
                                            </div>
                                          )}
                                          <div className="flex items-center space-x-2 mt-2">
                                            <Input
                                              type="number"
                                              min="1"
                                              step="1"
                                              value={item.quantity}
                                              onChange={(e) =>
                                                handleItemChange(
                                                  item.id,
                                                  "quantity",
                                                  e.target.value
                                                )
                                              }
                                              className="w-20 text-sm"
                                              placeholder="Cantidad"
                                            />
                                            <Select
                                              value={item.unit}
                                              onValueChange={(val) =>
                                                handleItemChange(
                                                  item.id,
                                                  "unit",
                                                  val
                                                )
                                              }
                                            >
                                              <SelectTrigger className="w-28 text-sm">
                                                <SelectValue placeholder="Unidad" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {PRODUCT_UNITS.map((unit) => (
                                                  <SelectItem
                                                    key={unit.value}
                                                    value={unit.value}
                                                  >
                                                    {unit.label}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleItemRemove(item.id)
                                          }
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-8 text-slate-500">
                                    <Layers className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                    <p>No hay items asociados</p>
                                    <p className="text-sm">
                                      Busca y selecciona items para asociar al
                                      producto
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    )}
                  </div>
                </Tabs>
              ) : (
                // Single visible section - render without tabs
                <div className="flex-1 overflow-y-auto">
                  {isSectionVisible("basic") && (
                    <div className="space-y-6 px-1">
                      {/* Render basic section content without TabsContent wrapper */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Same content as basic tab but without TabsContent */}
                      </div>
                    </div>
                  )}
                  {/* Add other single sections if needed */}
                </div>
              )}

              <DialogFooter className="flex-shrink-0 mt-6 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                >
                  {isSubmitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {mode === "create" ? "Crear Producto" : "Guardar Cambios"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 